import { api, ApiError } from './api';
import {
  PictureTooLargeError,
  sealFamilyPicture,
  sealGroupPicture,
  sealUserPicture,
} from './e2ee/pictures';
import { KeysNotReadyError } from './e2ee/groupInfo';
import { forgetScope, type ScopeKind } from './e2ee/session';
import { t } from '../i18n';

// Setting pictures (M72): each is sealed on this device before it goes up
// (D291, D292); the server refuses a readable one (D297). Removing one
// sends null.

/**
 * No family's key could hold my picture's key, so nobody could ever open
 * it (D292): nothing is uploaded, and the person is told to try again.
 */
export class NoPictureKeyError extends Error {
  constructor() {
    super('NO_PICTURE_KEY');
  }
}

/** I'm in no family yet, so no one could see a picture (D292). */
export class NoFamilyForPictureError extends Error {
  constructor() {
    super('NO_FAMILY');
  }
}

const staleKey = (err: unknown) =>
  err instanceof ApiError &&
  (err.code === 'STALE_KEY' || err.code === 'ROTATION_REQUIRED');

/** Seals and sends; once more with fresh keys if a key moved on meanwhile. */
async function sealedWrite<T>(
  scopes: Array<{ kind: ScopeKind; id: string }>,
  write: () => Promise<T>
): Promise<T> {
  try {
    return await write();
  } catch (err) {
    if (!staleKey(err)) throw err;
    scopes.forEach(({ kind, id }) => forgetScope(kind, id));
    return write();
  }
}

/** My picture: sealed once, its key wrapped for each of my families (D292). */
export function setMyPicture(
  userId: string,
  dataUrl: string | null,
  familyIds: string[]
) {
  if (!dataUrl) return api.updateProfile({ picture: null });
  if (familyIds.length === 0)
    return Promise.reject(new NoFamilyForPictureError());
  return sealedWrite(
    familyIds.map((id) => ({ kind: 'family' as const, id })),
    async () => {
      const { sealed, keys } = await sealUserPicture(
        userId,
        dataUrl,
        familyIds
      );
      // Its key lives only in this tab until wrapped; a reload would lose it
      // and leave a picture no one can open (D295).
      if (keys.length === 0) throw new NoPictureKeyError();
      return api.updateProfile({ picture: { sealed, keys } });
    }
  );
}

/** A family's picture, sealed with the family key (D291). */
export function setFamilyPicture(familyId: string, dataUrl: string | null) {
  if (!dataUrl) return api.updateFamily(familyId, { pictureSealed: null });
  return sealedWrite([{ kind: 'family', id: familyId }], async () =>
    api.updateFamily(familyId, {
      pictureSealed: await sealFamilyPicture(familyId, dataUrl),
    })
  );
}

/** A group's picture, sealed with the chat's key (D291). */
export function setGroupPicture(
  conversationId: string,
  dataUrl: string | null
) {
  if (!dataUrl)
    return api.updateConversation(conversationId, { picture: null });
  return sealedWrite([{ kind: 'chat', id: conversationId }], async () => {
    try {
      return await api.updateConversation(conversationId, {
        picture: await sealGroupPicture(conversationId, dataUrl),
      });
    } catch (err) {
      // Someone changed it meanwhile: the next revision after theirs (D326).
      const stored =
        err instanceof ApiError
          ? (err.details?.picture ?? err.details?.revision)
          : null;
      if (
        err instanceof ApiError &&
        err.code === 'STALE_REVISION' &&
        typeof stored === 'number'
      ) {
        return api.updateConversation(conversationId, {
          picture: await sealGroupPicture(conversationId, dataUrl, stored),
        });
      }
      throw err;
    }
  });
}

/** Why a picture couldn't be set, for the toast. */
export function pictureErrorMessage(err: unknown): string {
  if (err instanceof PictureTooLargeError) return t('e2ee.pictureTooLarge');
  if (err instanceof KeysNotReadyError) return t('e2ee.keysNotReady');
  if (err instanceof NoPictureKeyError) return t('e2ee.pictureNoFamilyKey');
  if (err instanceof NoFamilyForPictureError) return t('e2ee.pictureNoFamily');
  return err instanceof ApiError ? err.message : t('common.somethingWrong');
}
